import React, { useEffect, useRef } from "react";
import "./UnityGame.css";

type UnityGameProps = {
  buildPath: string;
};

interface UnityInstance {
  SendMessage(gameObject: string, methodName: string, parameter?: string): void;
  Quit(): Promise<void>;
}

declare function createUnityInstance(
  canvas: HTMLCanvasElement,
  config: {
    dataUrl: string;
    frameworkUrl: string;
    codeUrl: string;
    streamingAssetsUrl: string;
    webglContextAttributes?: WebGLContextAttributes;
  },
): Promise<UnityInstance>;

declare global {
  interface Window {
    unityInstance?: UnityInstance;
    OnBridgeReady?: () => void;
  }
}

export default function UnityGame({ buildPath }: UnityGameProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const unityInstanceRef = useRef<UnityInstance | null>(null);
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    let isUnmounted = false;
    let initTimeout: number | undefined;
    const gameFolder = buildPath.replace(/^\/+|\/+$/g, "");
    const baseUrl = `/RLPages/${gameFolder}`;
    const prefix = gameFolder;

    const script = document.createElement("script");
    script.src = `${baseUrl}/Build/${prefix}.loader.js`;
    const canvas = canvasRef.current;

    script.onload = () => {
      initTimeout = window.setTimeout(() => {
        if (isUnmounted) return;

        // 🔒 GPU-safe sizing
        const testCanvas = document.createElement("canvas");
        const gl =
          testCanvas.getContext("webgl") || testCanvas.getContext("webgl2");
        const maxSize = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 4096;

        const MAX_TEXTURE_SIZE = Math.min(maxSize / 2, 4096);

        const cssWidth = canvas.clientWidth || 960;
        const cssHeight = canvas.clientHeight || 600;

        canvas.width = Math.min(cssWidth, MAX_TEXTURE_SIZE);
        canvas.height = Math.min(cssHeight, MAX_TEXTURE_SIZE);

        console.log(
          `Canvas set to ${canvas.width}×${canvas.height} (max GPU ${maxSize})`,
        );

        createUnityInstance(canvas, {
          dataUrl: `${baseUrl}/Build/${prefix}.data`,
          frameworkUrl: `${baseUrl}/Build/${prefix}.framework.js`,
          codeUrl: `${baseUrl}/Build/${prefix}.wasm`,
          streamingAssetsUrl: `${baseUrl}/StreamingAssets`,
          webglContextAttributes: {
            preserveDrawingBuffer: false,
            powerPreference: "high-performance",
          },
        })
          .then((unityInstance) => {
            if (isUnmounted) {
              void unityInstance.Quit().catch((err) => {
                console.error("❌ Unity quit after unmount failed:", err);
              });
              return;
            }

            unityInstanceRef.current = unityInstance;
            window.unityInstance = unityInstance;
            console.log("✅ Unity instance created");

            const handleWheel = (e: WheelEvent) => {
              // Allow page scroll while hovering Unity
              window.scrollBy({
                top: e.deltaY,
                behavior: "auto",
              });
            };

            canvas.addEventListener("wheel", handleWheel, {
              passive: true,
            });
            wheelHandlerRef.current = handleWheel;
          })
          .catch((err) => {
            console.error("❌ Unity init failed:", err);
          });
      }, 100);
    };

    script.onerror = () => {
      console.error("❌ Failed to load Unity loader script");
    };

    document.body.appendChild(script);

    return () => {
      isUnmounted = true;
      if (initTimeout) {
        window.clearTimeout(initTimeout);
      }

      const currentWheelHandler = wheelHandlerRef.current;
      if (currentWheelHandler) {
        canvas.removeEventListener("wheel", currentWheelHandler);
      }
      wheelHandlerRef.current = null;

      const currentUnityInstance = unityInstanceRef.current;
      if (currentUnityInstance) {
        void currentUnityInstance
          .Quit()
          .then(() => {
            console.log("🛑 Unity instance quit cleanly");
          })
          .catch((err) => {
            console.error("❌ Unity quit failed:", err);
          })
          .finally(() => {
            unityInstanceRef.current = null;
            if (window.unityInstance === currentUnityInstance) {
              delete window.unityInstance;
            }
          });
      } else {
        delete window.unityInstance;
      }

      script.parentNode?.removeChild(script);
    };
  }, [buildPath]);

  useEffect(() => {
    console.log("🎮 UnityGame MOUNT");
    return () => {
      console.log("🧹 UnityGame UNMOUNT");
    };
  }, [buildPath]);

  return (
    <div className="unity-container">
      <canvas ref={canvasRef} id="unity-canvas" />
    </div>
  );
}
